import * as React from 'react';
import PhoneInput, { Country } from 'react-phone-number-input/mobile';
import { cn } from '@ordering/utils/styling';
import { useTranslations } from 'next-intl';

import { Body } from '@ordering/components/ui/Typography';
import { Alert } from '@sumup-oss/icons';

export interface MobileNumberInputProps extends 
  Omit<React.ComponentProps<typeof PhoneInput>, 'ref'> {
  className?: string;
  defaultCountry?: Country;
  error?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  children?: React.ReactNode;
}

const MobileNumberInput = React.forwardRef<HTMLInputElement, MobileNumberInputProps>(
  (
    { 
      id, 
      className, 
      defaultCountry, 
      error,
      onChange,
      required,
      children,
      ...props 
    }, ref
  ) => {
    const t = useTranslations();

    return (
      <div role="group" aria-labelledby={`${id}-label`}>
        <label id={`${id}-label`} htmlFor={id} aria-labelledby={`${id}-label`}>
          <Body variant="s" weight="medium" as="p" className="text-black mb-1">
            {t('checkout_page.notification.phone_number.label')}
            {required && <span className="text-red-500 text-sm" aria-hidden="true"> *</span>}
          </Body>
        </label>

        <PhoneInput
          id={id}
          defaultCountry={defaultCountry}
          international
          withCountryCallingCode
          countrycallingcodeposition="before"
          placeholder={t('checkout_page.notification.phone_number.placeholder')}
          className={cn(
            error && '!border-red-500',
            className
          )}
          onChange={onChange}
          aria-describedby="phone-number-hint"
          {...props}
        />
        
        {error && (
          <div id={`${id}-error`} className="flex items-start gap-2 mt-2 text-red-600">
            <Alert size="16" />
            <span className="text-sm leading-tight break-words">{error}</span>
          </div>
        )}

        {children}

      </div>
    );
  }
);

MobileNumberInput.displayName = 'MobileNumberInput';

export { MobileNumberInput };